import { useEffect, useRef, useState } from "react";
import { CloudSyncScene } from "@/components/CloudSyncScene";
import { Languages, UsersRound, Smartphone, Store, Network, ShieldCheck, Code2, Layers, ArrowUp, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Logo, Wordmark } from "@/components/Logo";
import { MODULES } from "@/pages/More";

function useReveal() {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setOn(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, on ? "reveal in" : "reveal"];
}

export const Separator = ({ white, center }) => (
  <div className={`flex items-center gap-2 my-6 ${center ? "justify-center" : ""}`} aria-hidden="true">
    <span className={`h-0.5 w-40 sm:w-56 rounded ${white ? "bg-white" : "bg-[#E9772A]"}`} />
    <span className={`text-sm font-bold leading-none ${white ? "text-white" : "text-[#E9772A]"}`}>+</span>
  </div>
);

function Section({ id, className = "", children }) {
  const [ref, cls] = useReveal();
  return (
    <section id={id} data-testid={`landing-${id}`} className={`scroll-mt-20 py-16 sm:py-24 ${className}`}>
      <div ref={ref} className={`max-w-6xl mx-auto px-5 ${cls}`}>{children}</div>
    </section>
  );
}

const Title = ({ children, className = "" }) => <h3 className={`font-heading text-2xl sm:text-3xl uppercase ${className}`}>{children}</h3>;

export function ModulesSection() {
  const { t } = useApp();
  return (
    <Section id="modules" className="bg-white">
      <div className="text-center">
        <Title className="text-[#0B2A5B]">{t("landing_modules_title")}</Title>
        <Separator center />
        <p className="text-lg text-muted-foreground">{t("landing_modules_q")}</p>
        <p className="text-lg font-bold text-[#0B2A5B]">{t("landing_modules_q2")}</p>
      </div>
      <ul className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
        {MODULES.map(({ icon: Icon, label, desc }, i) => (
          <li key={label} data-testid={`landing-module-${label}`} style={{ transitionDelay: `${i * 60}ms` }}
            className="module-tile group rounded-2xl border bg-[#F5F8FF] p-5 flex flex-col items-center text-center gap-3 hover:-translate-y-1 hover:shadow-lg hover:border-[#F28C28]/40">
            <Icon className="w-11 h-11 text-[#0B2A5B] group-hover:text-[#F28C28] transition-colors" strokeWidth={1.25} />
            <span className="font-bold text-[#0B2A5B] leading-tight">{t(label)}</span>
            <span className="text-xs text-muted-foreground leading-snug">{t(desc)}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Mascot() {
  return (
    <div className="mascot relative w-48 h-48 sm:w-60 sm:h-60 mx-auto" aria-hidden="true" data-testid="landing-ai-mascot">
      <div className="absolute inset-[14%] rounded-[38%] bg-[#0B2A5B] shadow-2xl shadow-[#0B2A5B]/30 mascot-bob">
        <span className="absolute left-1/2 -top-5 -translate-x-1/2 w-1 h-5 bg-[#0B2A5B]" />
        <span className="absolute left-1/2 -top-8 -translate-x-1/2 w-4 h-4 rounded-full bg-[#F28C28] mascot-ping" />
        <div className="absolute inset-x-[16%] top-[26%] h-[34%] rounded-2xl bg-[#061436] flex items-center justify-around px-3">
          <span className="w-4 h-6 rounded-full bg-[#3B9BFF] mascot-eye" />
          <span className="w-4 h-6 rounded-full bg-[#3B9BFF] mascot-eye" />
        </div>
        <span className="absolute left-1/2 bottom-[16%] -translate-x-1/2 w-10 h-2 rounded-full bg-[#F28C28]" />
      </div>
      <div className="absolute -top-2 -end-4 rounded-2xl rounded-es-none bg-white shadow-lg px-4 py-3 flex gap-1.5">
        {[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full bg-[#0B2A5B] typing-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
      </div>
    </div>
  );
}

export function AiSection() {
  const { t } = useApp();
  return (
    <Section id="ai" className="bg-[#EAF2FF]">
      <div className="grid md:grid-cols-[1fr_1.4fr] gap-10 items-center">
        <Mascot />
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F28C28]/10 text-[#F28C28] px-3 py-1 text-xs font-bold uppercase" data-testid="landing-ai-soon">
            <Sparkles className="w-3.5 h-3.5" />{t("ai_soon")}
          </span>
          <Title className="text-[#0B2A5B] mt-4">
            {t("ai_title")} <span className="ai-name"><span>N</span>O<span>U</span>R</span>
          </Title>
          <Separator />
          <p className="text-muted-foreground leading-relaxed">{t("ai_text")}</p>
          <p className="text-muted-foreground leading-relaxed mt-4">{t("ai_text2")}</p>
        </div>
      </div>
    </Section>
  );
}

const MULTI = [
  { icon: Languages, k: "multi_lang" }, { icon: UsersRound, k: "multi_role" },
  { icon: Smartphone, k: "multi_device" }, { icon: Store, k: "multi_type" },
];
const ORBIT_LANGS = ["عربي", "حسانية", "ES", "PIN"];

export function MultiSection() {
  const { t } = useApp();
  return (
    <Section id="multi" className="bg-white">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="relative w-64 h-64 sm:w-80 sm:h-80 mx-auto" aria-hidden="true">
          <div className="globe absolute inset-[12%] rounded-full" data-testid="landing-globe" />
          <div className="globe-orbit absolute inset-0">
            {ORBIT_LANGS.map((l, i) => (
              <span key={l} className="globe-chip absolute w-14 h-14 -ms-7 -mt-7 rounded-full bg-[#F28C28] text-white text-xs font-bold grid place-items-center shadow-lg"
                style={{ left: `${50 + 50 * Math.cos((i * Math.PI) / 2)}%`, top: `${50 + 50 * Math.sin((i * Math.PI) / 2)}%` }}>{l}</span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-[#F28C28] uppercase">{t("multi_kicker")}</p>
          <Title className="text-[#0B2A5B] mt-2">{t("multi_title")}</Title>
          <Separator />
          <p className="text-muted-foreground">{t("multi_text")}</p>
          <ul className="mt-6 space-y-3">
            {MULTI.map(({ icon: Icon, k }) => (
              <li key={k} className="flex items-center gap-3" data-testid={`landing-${k}`}>
                <span className="w-10 h-10 shrink-0 rounded-full bg-[#EAF2FF] text-[#0B2A5B] grid place-items-center"><Icon className="w-5 h-5" /></span>
                <span className="font-medium text-[#0B2A5B]">{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

export function DiffSection() {
  const { t } = useApp();
  return (
    <Section id="diff" className="bg-[#E9772A] text-white">
      <div className="max-w-3xl mx-auto text-center">
        <Title>{t("diff_kicker")}</Title>
        <Separator white center />
        <p className="text-lg text-white/90 leading-relaxed">{t("diff_text")}</p>
      </div>
    </Section>
  );
}

function SyncDiagram() {
  return <CloudSyncScene className="max-w-lg mx-auto" />;
}

const IconBlock = ({ icon: Icon, text, testid }) => (
  <div className="flex flex-col items-center text-center gap-4" data-testid={testid}>
    <Icon className="w-10 h-10" strokeWidth={1.25} />
    <p className="font-light leading-relaxed">{text}</p>
  </div>
);

export function SaasSection() {
  const { t } = useApp();
  return (
    <Section id="cloud" className="bg-[#0A2550] text-white">
      <div className="max-w-2xl mx-auto text-center">
        <Title>SaaS</Title>
        <Separator center />
        <p className="text-white/90">{t("saas_text")}</p>
      </div>
      <div className="mt-12 grid lg:grid-cols-[2fr_1fr] gap-12 items-center">
        <SyncDiagram />
        <div className="space-y-10">
          <IconBlock icon={Network} text={t("saas_offline")} testid="landing-saas-offline" />
          <IconBlock icon={ShieldCheck} text={t("saas_secure")} testid="landing-saas-secure" />
        </div>
      </div>
    </Section>
  );
}

const CODE = [
  ["#F28C28", "sync", ".push(ops)"], ["#3B9BFF", "queue", ".offline = true"], ["#FFB347", "receipt", ".print()"],
  ["#5EE3A1", "label", ".barcode(EAN13)"], ["#3B9BFF", "report", ".pdf()"],
];

export function TechSection() {
  const { t } = useApp();
  return (
    <Section id="tech" className="bg-[#EAF2FF]">
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
        <div>
          <Title className="text-[#0B2A5B]">Tech stack</Title>
          <Separator />
          <div className="flex gap-4"><Code2 className="w-9 h-9 shrink-0 text-[#0B2A5B]" strokeWidth={1.25} /><p className="text-muted-foreground">{t("tech_code")}</p></div>
          <div className="flex gap-4 mt-6"><Layers className="w-9 h-9 shrink-0 text-[#0B2A5B]" strokeWidth={1.25} /><p className="text-muted-foreground">{t("tech_platform")}</p></div>
        </div>
        <div dir="ltr" className="rounded-2xl bg-[#061436] shadow-2xl shadow-[#0B2A5B]/30 overflow-hidden" data-testid="landing-code-window">
          <div className="flex gap-2 px-4 py-3 bg-[#0B2A5B]">{["#F28C28", "#FFB347", "#5EE3A1"].map((c) => <span key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />)}</div>
          <div className="p-6 font-mono-receipt text-sm sm:text-base space-y-3">
            {CODE.map(([c, a, b], i) => (
              <div key={a} className="code-line overflow-hidden whitespace-nowrap" style={{ animationDelay: `${i * 0.9}s` }}>
                <span className="text-white/40 me-3">{i + 1}</span><span style={{ color: c }}>{a}</span><span className="text-white">{b}</span>
              </div>
            ))}
            <span className="inline-block w-2.5 h-5 bg-[#F28C28] align-middle caret" />
          </div>
        </div>
      </div>
    </Section>
  );
}

export function CloudParallax() {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const p = (window.innerHeight - r.top) / (window.innerHeight + r.height);
        el.style.setProperty("--shift", `${(p - 0.5) * 40}%`);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={ref} dir="ltr" className="cloud-parallax relative overflow-hidden bg-white py-10 sm:py-16" data-testid="landing-cloud-parallax" aria-hidden="true">
      <div className="huge-word font-heading text-[28vw] leading-none text-center text-[#0B2A5B]/[0.07] whitespace-nowrap">Cloud</div>
    </div>
  );
}

export function LandingFooter() {
  const { t } = useApp();
  return (
    <footer className="bg-[#0B2A5B] text-white/80 text-sm" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row gap-2 justify-between">
        <span className="font-bold text-white flex items-center gap-2"><Logo className="w-7 h-7" /><Wordmark size="sm" /></span>
        <span>{t("footer_note")}</span>
      </div>
    </footer>
  );
}

export const NAV = [["modules", "nav_modules"], ["ai", "nav_ai"], ["multi", "nav_multi"], ["cloud", "nav_cloud"], ["tech", "nav_tech"]];
export const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export function BackToTop() {
  const { t } = useApp();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label={t("back_top")} data-testid="landing-back-to-top"
      className={`fixed bottom-5 end-5 z-40 w-12 h-12 rounded-full bg-[#F28C28] text-white grid place-items-center shadow-lg transition-[opacity,transform] duration-300 ${show ? "opacity-100" : "opacity-0 translate-y-4 pointer-events-none"}`}>
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}
